import { useConfirmation } from '@/hooks/useConfirmation';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { CAPABILITY_BADGES, isAutoFillable, type FillCapability } from '@/constants/fillCapability';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  v2,
  parseApiErrorBody,
  type GovernmentForm,
  type GovFormMapping,
  type GovFormMappingPath,
  type GovFormTestResult,
  type Submission,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { formatDate } from '@/lib/format';

import { REGIONS } from '@/constants/regions';
import { OfficialFormWorkflows } from '@/components/forms/OfficialFormWorkflows';

export interface GovernmentFormsTabProps {
  readonly canWrite: boolean;
  readonly canDelete: boolean;
}

interface UploadDraft {
  region: string;
  formKey: string;
  formName: string;
  file: File | null;
  allowManualStorage: boolean;
}

const EMPTY_UPLOAD: UploadDraft = {
  region: 'FDA',
  formKey: '',
  formName: '',
  file: null,
  allowManualStorage: false,
};

export function GovernmentFormsTab({
  canWrite,
  canDelete,
}: GovernmentFormsTabProps): ReactElement {
  const { ask, dialog } = useConfirmation();
  const [forms, setForms] = useState<GovernmentForm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [mappings, setMappings] = useState<GovFormMapping[] | null>(null);
  const [mappingEdits, setMappingEdits] = useState<Record<string, Partial<GovFormMapping>>>({});

  const [upload, setUpload] = useState<UploadDraft>(EMPTY_UPLOAD);
  const [uploading, setUploading] = useState(false);
  const [uploadFileKey, setUploadFileKey] = useState(0);
  const [search, setSearch] = useState('');

  const [testResults, setTestResults] = useState<GovFormTestResult[] | null>(null);
  // Tracker row 4 — the preview runs against a REAL submission of the form's
  // region (never sample data), through the same resolver generation uses.
  const [previewSubs, setPreviewSubs] = useState<Submission[] | null>(null);
  const [previewSubId, setPreviewSubId] = useState<string>('');
  const [pathOptions, setPathOptions] = useState<GovFormMappingPath[]>([]);

  const reload = useCallback((): void => {
    setError(null);
    v2.govForms
      .list()
      .then(setForms)
      .catch((e: unknown) => setError(formatApiError(e, "Operation failed")));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!selectedFormId) {
      setMappings(null);
      setMappingEdits({});
      return;
    }
    setMappings(null);
    setMappingEdits({});
    v2.govForms
      .getMappings(selectedFormId)
      .then(setMappings)
      .catch((e: unknown) => setError(formatApiError(e, "Operation failed")));
  }, [selectedFormId]);

  const selectedRegion = forms?.find((f) => f.Id === selectedFormId)?.Region ?? null;

  // Row 4 — submissions to preview against: the form's region, most recently
  // updated first (the default).
  useEffect(() => {
    setPreviewSubs(null);
    setPreviewSubId('');
    if (!selectedRegion) return;
    let cancelled = false;
    v2.submissions
      .list({ region: selectedRegion, limit: 200 })
      .then((res) => {
        if (cancelled) return;
        const recent = [...res.data].sort((a, b) =>
          (b.UpdatedAt ?? b.CreatedAt ?? '').localeCompare(a.UpdatedAt ?? a.CreatedAt ?? ''),
        );
        setPreviewSubs(recent);
        setPreviewSubId(recent[0]?.Id ?? '');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPreviewSubs([]);
        setError(formatApiError(e, 'Could not load submissions to preview against.'));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRegion]);

  // Row 4 — the canonical path vocabulary for the searchable path picker.
  useEffect(() => {
    if (!selectedFormId || pathOptions.length > 0) return;
    v2.govForms
      .mappingPaths()
      .then((r) => setPathOptions(r.paths))
      .catch(() => setPathOptions([]));
  }, [selectedFormId, pathOptions.length]);

  const handleUpload = async (): Promise<void> => {
    const formKey = upload.formKey.trim() || suggestedFormKey(upload.region, upload.formName);
    if (!upload.file || !upload.region || !upload.formName.trim() || !formKey) {
      setError('Choose a market, enter a form name, and select a PDF file. If a name cannot produce an identifier, set one under Advanced settings.');
      return;
    }
    const existing = forms?.find((f) => f.FormKey === formKey);
    if (existing && !(await ask({ title: 'Replace official form PDF', message: `This identifier belongs to “${existing.FormName}”. Uploading will replace its library PDF and recheck its mappings. To add a separate form instead, choose a different identifier under Advanced settings.`, confirmLabel: 'Replace PDF', variant: 'destructive' }))) return;
    setUploading(true);
    setError(null);
    try {
      // Identify and upload use the same explicit storage mode. A refused
      // file is never sent to upload-pdf; storage does not enable filling.
      try {
        await v2.govForms.identifyPdf(upload.file, upload.allowManualStorage ? { allowManualStorage: true } : undefined);
      } catch (e) {
        setError(preflightMessage(e));
        return;
      }
      const res = await v2.govForms.uploadPdf(upload.file, {
        region: upload.region,
        formKey,
        formName: upload.formName.trim(),
        ...(upload.allowManualStorage ? { allowManualStorage: true } : {}),
      });
      // Row 5 — a revised form that orphaned mappings says so right away.
      const broken = res.revision?.brokenMappings.length ?? 0;
      setNotice(
        broken > 0
          ? `${res.form.FormName} was updated. ${broken} mapping${broken === 1 ? '' : 's'} no longer match this form — open Mappings to remap ${broken === 1 ? 'it' : 'them'}.`
          : !isAutoFillable(res.form.FillCapability) ? `${res.form.FormName} was saved for external completion. Automated filling is disabled; download the original and upload the completed document to your submission.` : null,
      );
      setUpload(EMPTY_UPLOAD);
      setUploadFileKey((key) => key + 1);
      reload();
      if (broken > 0 && res.form.Id === selectedFormId) {
        void v2.govForms.getMappings(res.form.Id).then(setMappings);
      }
    } catch (e) {
      setError(formatApiError(e, "Operation failed"));
    } finally {
      setUploading(false);
    }
  };

  // ISS-012 — destructive delete goes through the shared confirmation
  // dialog (useConfirmation), not the native browser confirm().
  const handleDelete = async (id: string): Promise<void> => {
    if (!(await ask({ title: 'Delete government form', message: 'Delete this government form and all of its mappings?', confirmLabel: 'Delete', variant: 'destructive' }))) return;
    try {
      await v2.govForms.delete(id);
      if (selectedFormId === id) setSelectedFormId(null);
      reload();
    } catch (e) {
      setError(formatApiError(e, "Operation failed"));
    }
  };

  /** The mapping rows with unsaved edits applied, in the shape save sends. */
  const mergedMappings = (): Array<
    Pick<GovFormMapping, 'PdfFieldName' | 'AutoeCTDFieldPath' | 'AutoFillEnabled' | 'IsRequired'>
  > =>
    (mappings ?? []).map((m) => {
      const edit = mappingEdits[m.PdfFieldName] ?? {};
      return {
        PdfFieldName: m.PdfFieldName,
        AutoeCTDFieldPath:
          edit.AutoeCTDFieldPath !== undefined ? edit.AutoeCTDFieldPath : m.AutoeCTDFieldPath,
        AutoFillEnabled:
          edit.AutoFillEnabled !== undefined ? edit.AutoFillEnabled : m.AutoFillEnabled,
        // Sent back as-is: the server treats an omitted flag as "not required".
        IsRequired: m.IsRequired === true,
      };
    });

  const saveMerged = async (
    rows: ReturnType<typeof mergedMappings>,
  ): Promise<void> => {
    if (!selectedFormId) return;
    try {
      const updated = await v2.govForms.saveMappings(selectedFormId, rows);
      setMappings(updated);
      setMappingEdits({});
      reload();
    } catch (e) {
      setError(formatApiError(e, "Operation failed"));
    }
  };

  const handleSaveMappings = async (): Promise<void> => {
    if (!selectedFormId || !mappings) return;
    await saveMerged(mergedMappings());
  };

  /**
   * Tracker row 5 — "Remap to <new field>": the broken mapping's path,
   * auto-fill and required flag move onto the field it most likely became,
   * and the broken row goes (its field is not in the PDF any more).
   */
  const handleRemap = async (brokenField: string, toField: string): Promise<void> => {
    const rows = mergedMappings();
    const broken = rows.find((r) => r.PdfFieldName === brokenField);
    if (!broken) return;
    await saveMerged(
      rows
        .filter((r) => r.PdfFieldName !== brokenField)
        .map((r) =>
          r.PdfFieldName === toField
            ? {
                ...r,
                AutoeCTDFieldPath: broken.AutoeCTDFieldPath,
                AutoFillEnabled: broken.AutoFillEnabled,
                IsRequired: broken.IsRequired,
              }
            : r,
        ),
    );
  };

  const handleTestMapping = async (): Promise<void> => {
    if (!selectedFormId || !previewSubId || Object.keys(mappingEdits).length > 0) return;

    try {
      const results = await v2.govForms.testMapping(selectedFormId, previewSubId);
      setTestResults(results);
    } catch (e) {
      setError(formatApiError(e, "Operation failed"));
    }
  };

  const previewSub = previewSubs?.find((s) => s.Id === previewSubId) ?? null;
  const brokenMappings = (mappings ?? []).filter((m) => m.IsBroken);

  // Forms grouped by region for the table.
  const visibleForms = (forms ?? []).filter((f) => `${f.FormName} ${f.FormKey} ${f.Region}`.toLowerCase().includes(search.trim().toLowerCase()));
  const groupedForms: Array<{ region: string; forms: GovernmentForm[] }> = REGIONS.map((r) => ({
    region: r,
    forms: visibleForms.filter((f) => f.Region === r),
  })).filter((g) => g.forms.length > 0);
  const ungrouped = visibleForms.filter((f) => !REGIONS.some((r) => r === f.Region));

  const selected = forms?.find((f) => f.Id === selectedFormId) ?? null;

  return (
    <section aria-label="Government Forms">
      {dialog}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div
          role="status"
          data-testid="gov-form-notice"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: 'var(--amber-bg, #FEF3C7)',
            color: 'var(--amber, #B45309)',
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}

      <OfficialFormWorkflows />
      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Add the official PDF to your tenant’s library. For supported forms, map the fields to saved information and test them against a submission. Unsupported forms can be kept for download and external completion. Uploading alone does not qualify a form for filing.</p>
      {/* Upload area */}
      {canWrite && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            background: 'var(--surface2)',
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Upload official form PDF</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              Region
              <select
                value={upload.region}
                onChange={(e) => setUpload((p) => ({ ...p, region: e.target.value }))}
                aria-label="Region"
                style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
              >
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              Form name
              <input
                value={upload.formName}
                onChange={(e) => setUpload((p) => ({ ...p, formName: e.target.value }))}
                aria-label="Form name"
                placeholder="FDA Form 1571 — IND Application"
                style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              PDF
              <input
                key={uploadFileKey}
                type="file"
                accept="application/pdf"
                aria-label="PDF file"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setUpload((p) => ({ ...p, file: f, formName: p.formName || f?.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ') || '' }));
                }}
              />
            </label>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !upload.file || forms === null}
              style={{
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: uploading ? 'wait' : 'pointer',
              }}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          <label style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
            <input type="checkbox" checked={upload.allowManualStorage}
              onChange={e => setUpload(p => ({ ...p, allowManualStorage: e.target.checked }))} />{' '}
            Keep unsupported PDFs for external completion
          </label>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Stores the original encrypted, XFA or flat PDF without enabling automatic filling. Review and complete it using the official instructions.</p>
          <details style={{ marginTop: 12, fontSize: 13 }}>
            <summary style={{ cursor: 'pointer' }}>Advanced settings</summary>
            <label style={{ display: 'block', marginTop: 8 }}>Form key
              <input aria-label="Form key" value={upload.formKey}
                placeholder={suggestedFormKey(upload.region, upload.formName) || 'Generated from the market and form name'}
                onChange={(e) => setUpload((p) => ({ ...p, formKey: e.target.value }))}
                style={{ display: 'block', padding: '6px 10px', width: 'min(420px, 100%)', border: '1px solid var(--border)', borderRadius: 6 }} />
            </label>
            <p style={{ color: 'var(--text-muted)' }}>An identifier is generated automatically. Use an existing form’s identifier only when replacing its PDF; mappings will be rechecked.</p>
          </details>
        </div>
      )}

      <label style={{ display: 'block', marginBottom: 12 }}>Find an uploaded form
        <input type="search" aria-label="Find an uploaded form" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, identifier or market" style={{ display: 'block', padding: '6px 10px', width: 'min(420px, 100%)', border: '1px solid var(--border)', borderRadius: 6 }} />
      </label>
      {forms !== null && forms.length > 0 && visibleForms.length === 0 && <p>No uploaded forms match your search.</p>}
      {/* Forms list */}
      {forms === null ? (
        <p>Loading forms…</p>
      ) : forms.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No government forms uploaded yet.</p>
      ) : (
        <div className="table-wrap">
          <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Form name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Region</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Fields</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Auto-fill</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Updated</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...groupedForms, ...(ungrouped.length ? [{ region: 'Other', forms: ungrouped }] : [])].map((g) => (
                <FormGroupRows
                  key={g.region}
                  group={g}
                  canDelete={canDelete}
                  selectedFormId={selectedFormId}
                  onSelect={(id) => setSelectedFormId(id)}
                  onDelete={(form) => void handleDelete(form.Id)}
                  onDownload={(id) => { void v2.govForms.downloadOriginal(id, forms?.find(f => f.Id === id)?.FormName).catch(e => setError(formatApiError(e, 'Could not download the original form.'))); }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mappings panel */}
      {selected && mappings && isAutoFillable(selected.FillCapability) && (
        <div
          aria-label="Mappings panel"
          style={{
            marginTop: 24,
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <h3 style={{ margin: 0 }}>{selected.FormName} — field mappings</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {previewSubs === null ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading submissions…</span>
              ) : previewSubs.length === 0 ? (
                <span data-testid="preview-no-submissions" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  No {selected.Region} submissions yet — create one to preview this form against real data.
                </span>
              ) : (
                <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                  Preview against submission
                  <select
                    aria-label="Preview against submission"
                    value={previewSubId}
                    onChange={(e) => setPreviewSubId(e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, maxWidth: 260 }}
                  >
                    {previewSubs.map((s) => (
                      <option key={s.Id} value={s.Id}>
                        {submissionLabel(s)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                onClick={handleTestMapping}
                disabled={!previewSubId || Object.keys(mappingEdits).length > 0}
                title={Object.keys(mappingEdits).length > 0 ? 'Save mappings before testing your changes' : 'Test saved mappings using the selected submission'}

                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: !previewSubId || Object.keys(mappingEdits).length > 0 ? 'not-allowed' : 'pointer',

                  background: 'var(--surface)',
                }}
              >
                Test mapping
              </button>
              {canWrite && (
                <button
                  type="button"
                  onClick={handleSaveMappings}
                  disabled={Object.keys(mappingEdits).length === 0}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: Object.keys(mappingEdits).length === 0 ? 'not-allowed' : 'pointer',
                    background: 'var(--primary)',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  Save mappings
                </button>
              )}
            </div>
          </div>
          {/* Row 5 — persistent banner while any mapping's field is gone from
              the PDF: names each one, with a one-click remap when the server
              suggests the field it most likely became. */}
          {brokenMappings.length > 0 && (
            <div
              role="region"
              aria-label="Broken mappings"
              data-testid="broken-mappings-banner"
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid var(--red)',
                background: 'var(--red-bg, #FEE2E2)',
                fontSize: 12,
              }}
            >
              <strong>
                {brokenMappings.length} mapping{brokenMappings.length === 1 ? '' : 's'} no longer
                match{brokenMappings.length === 1 ? 'es' : ''} this form
              </strong>
              <span style={{ color: 'var(--text-muted)' }}>
                {' '}
                — the uploaded PDF no longer has {brokenMappings.length === 1 ? 'this field' : 'these fields'}, so
                generation cannot fill {brokenMappings.length === 1 ? 'it' : 'them'}.
              </span>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {brokenMappings.map((b) => (
                  <li key={b.PdfFieldName} style={{ marginBottom: 4 }}>
                    <code>{b.PdfFieldName}</code> → {b.AutoeCTDFieldPath ?? '(no path)'}
                    {b.SuggestedField ? (
                      <>
                        {' '}
                        — probably renamed to <code>{b.SuggestedField}</code>{' '}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => void handleRemap(b.PdfFieldName, b.SuggestedField!)}
                            style={{
                              marginLeft: 4,
                              padding: '2px 8px',
                              border: '1px solid var(--border)',
                              borderRadius: 4,
                              background: 'var(--surface)',
                              cursor: 'pointer',
                              fontSize: 11,
                            }}
                          >
                            Remap to {b.SuggestedField}
                          </button>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}> — no similar field found; map the new field by hand.</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Row 4 — the searchable path picker: the canonical vocabulary the
              server validates against on save. */}
          <datalist id="gov-mapping-paths" data-testid="gov-mapping-paths">
            {pathOptions.map((p) => (
              <option key={p.path} value={p.path}>
                {p.group}
              </option>
            ))}
          </datalist>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {Object.keys(mappingEdits).length > 0 ? 'Save mappings before testing your changes. ' : ''}
            Test mapping uses the saved mappings and the selected submission.
          </p>

          <table style={{ width: '100%', marginTop: 12, fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface2)' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>PDF field</th>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>AutoeCTD field path</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Auto-fill</th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: 12, color: 'var(--text-muted)' }}>
                    No fields detected. Re-upload the PDF to populate mappings.
                  </td>
                </tr>
              ) : (
                mappings.map((m) => {
                  const edit = mappingEdits[m.PdfFieldName] ?? {};
                  const path =
                    edit.AutoeCTDFieldPath !== undefined ? edit.AutoeCTDFieldPath : m.AutoeCTDFieldPath;
                  const enabled =
                    edit.AutoFillEnabled !== undefined ? edit.AutoFillEnabled : m.AutoFillEnabled;
                  return (
                    <tr
                      key={m.PdfFieldName}
                      data-testid={`mapping-row-${m.PdfFieldName}`}
                      style={{
                        borderTop: '1px solid var(--border)',
                        ...(m.IsBroken && { background: 'var(--red-bg, #FEE2E2)', color: 'var(--red)' }),
                      }}
                    >
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                        {m.PdfFieldName}
                        {m.IsBroken && (
                          <span
                            data-testid={`broken-badge-${m.PdfFieldName}`}
                            title={m.BrokenReason ?? undefined}
                            style={{
                              marginLeft: 6,
                              padding: '1px 6px',
                              borderRadius: 8,
                              fontFamily: 'inherit',
                              fontSize: 10,
                              fontWeight: 600,
                              background: 'var(--red)',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Field no longer in PDF
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {canWrite ? (
                          <input
                            value={path ?? ''}
                            onChange={(e) =>
                              setMappingEdits((p) => ({
                                ...p,
                                [m.PdfFieldName]: {
                                  ...(p[m.PdfFieldName] ?? {}),
                                  AutoeCTDFieldPath: e.target.value || null,
                                },
                              }))
                            }
                            aria-label={`mapping-path-${m.PdfFieldName}`}
                            list="gov-mapping-paths"
                            placeholder="Search: submission.…, company.…, date"
                            style={{
                              width: '100%',
                              padding: '4px 6px',
                              border: '1px solid var(--border)',
                              borderRadius: 4,
                              fontSize: 11,
                            }}
                          />
                        ) : (
                          path ?? <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          disabled={!canWrite}
                          aria-label={`autofill-${m.PdfFieldName}`}
                          onChange={(e) =>
                            setMappingEdits((p) => ({
                              ...p,
                              [m.PdfFieldName]: {
                                ...(p[m.PdfFieldName] ?? {}),
                                AutoFillEnabled: e.target.checked,
                              },
                            }))
                          }
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Test mapping preview modal */}
      {testResults !== null && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="formTemplates.testMapping"
          title="Test mapping preview"
          ariaLabel="Test mapping preview"
          onClose={() => setTestResults(null)}
          width="min(800px, 90vw)"
          maxHeight="80vh"
          zIndex={1000}
          headerRight={
            <button
              type="button"
              onClick={() => setTestResults(null)}
              aria-label="Close test mapping preview"
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
            >
              ×
            </button>
          }
        >
          <>
            {previewSub && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                Previewed against <strong>{submissionLabel(previewSub)}</strong> and its company
                profile — exactly what generating this form for it would fill.
              </p>
            )}
            <table style={{ width: '100%', fontSize: 12, marginTop: 12 }}>
              <thead>
                <tr style={{ background: 'var(--surface2)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>PDF field</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>Mapped to</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>Preview value</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px' }}>Why</th>
                </tr>
              </thead>
              <tbody>
                {testResults.map((r) => (
                  <tr key={r.pdfFieldName} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                      {r.pdfFieldName}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      {r.mappedTo ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      {r.resolvedValue ?? <span style={{ color: 'var(--text-muted)' }}>(empty)</span>}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <StatusPill status={r.status} />
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        </MovableDialog>
      )}
    </section>
  );
}

/**
 * The identify pre-flight's refusal, worded by the server. A PDF with no fields
 * because it is encrypted or dynamic XFA also gets the server's reason (the
 * same wording as the capability badge); a plain flat PDF needs no more.
 */
function preflightMessage(e: unknown): string {
  const body = parseApiErrorBody(e) as
    | { data?: { fillCapability?: string; detail?: string } }
    | null;
  const base = formatApiError(e, 'This file could not be checked. Try again.');
  const detail = body?.data?.detail;
  return detail && detail !== base && body?.data?.fillCapability !== 'no-fields' ? `${base} ${detail}` : base;
}

/** How a submission is named in the preview picker (drug — sequence). */
function submissionLabel(s: Submission): string {
  return `${s.DrugName} — sequence ${s.SequenceNumber}`;
}

function StatusPill({ status }: { status: GovFormTestResult['status'] }): ReactElement {
  const colors: Record<typeof status, [string, string]> = {
    filled: ['#dcfce7', '#166534'],
    empty: ['#f1f5f9', '#475569'],
    'unknown-path': ['#fee2e2', '#991b1b'],
  };
  const [bg, fg] = colors[status];
  return (
    <span
      style={{
        background: bg,
        color: fg,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

function FormGroupRows({
  group,
  canDelete,
  selectedFormId,
  onSelect,
  onDelete,
  onDownload,
}: {
  group: { region: string; forms: GovernmentForm[] };
  canDelete: boolean;
  selectedFormId: string | null;
  onSelect: (id: string) => void;
  onDelete: (form: GovernmentForm) => void;
  onDownload: (id: string) => void;
}): ReactElement {
  return (
    <>
      <tr>
        <td
          colSpan={6}
          style={{
            background: 'var(--surface2)',
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-muted)',
          }}
        >
          {group.region}
        </td>
      </tr>
      {group.forms.map((f) => (
        <tr
          key={f.Id}
          style={{
            background: f.Id === selectedFormId ? 'var(--surface2)' : undefined,
            borderTop: '1px solid var(--border)',
          }}
          data-testid={`gov-form-row-${f.FormKey}`}
        >
          <td style={{ padding: '8px 12px' }}>
            <strong>{f.FormName}</strong>
            {/* Row 5 — mappings orphaned by a re-upload. */}
            {(f.BrokenMappingCount ?? 0) > 0 && (
              <span
                data-testid={`broken-icon-${f.FormKey}`}
                role="img"
                aria-label={`${f.BrokenMappingCount} mapping(s) no longer match this form`}
                title={`${f.BrokenMappingCount} mapping(s) no longer match this form — open Mappings to remap`}
                style={{ marginLeft: 6, color: 'var(--red)', fontWeight: 700 }}
              >
                ⚠ {f.BrokenMappingCount}
              </span>
            )}
            {/* phase-fix-unfillable-form-detection — honest capability badge:
                XFA/encrypted forms cannot be auto-filled and say so here
                instead of failing silently at generate time. */}
            {f.FillCapability && CAPABILITY_BADGES[f.FillCapability as FillCapability] && (
              <span
                data-testid={`capability-badge-${f.FormKey}`}
                title={
                  CAPABILITY_BADGES[f.FillCapability as FillCapability].tone === 'warn'
                    ? 'Automated fill unavailable — download, fill in Adobe Reader, and upload the completed PDF.'
                    : 'This form has standard fillable fields; automated fill is available.'
                }
                style={{
                  marginLeft: 8,
                  padding: '1px 7px',
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  background:
                    CAPABILITY_BADGES[f.FillCapability as FillCapability].tone === 'warn'
                      ? 'var(--amber-bg, #FEF3C7)'
                      : 'var(--green-bg, #DCFCE7)',
                  color:
                    CAPABILITY_BADGES[f.FillCapability as FillCapability].tone === 'warn'
                      ? 'var(--amber, #B45309)'
                      : 'var(--green, #15803D)',
                }}
              >
                {CAPABILITY_BADGES[f.FillCapability as FillCapability].label}
              </span>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              {f.FormKey}
            </div>
          </td>
          <td style={{ padding: '8px 12px' }}>{f.Region}</td>
          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{f.FieldCount}</td>
          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{f.AutoFillCount}</td>
          <td style={{ padding: '8px 12px' }}>{formatDate(f.UpdatedAt)}</td>
          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => onSelect(f.Id)}
              disabled={!isAutoFillable(f.FillCapability)}
              style={{
                padding: '4px 10px',
                border: '1px solid var(--border)',
                borderRadius: 4,
                cursor: 'pointer',
                background: 'var(--surface)',
                marginRight: 6,
              }}
            >
              Mappings
            </button>
            <button type="button" onClick={() => onDownload(f.Id)} aria-label={`Download ${f.FormName}`} disabled={!f.PdfBlobKey}
              style={{ padding: '4px 10px', marginRight: 6 }}>Download original</button>
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(f)}
                aria-label={`Delete ${f.FormName}`}
                style={{
                  padding: '4px 10px',
                  border: '1px solid var(--red)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: 'var(--surface)',
                  color: 'var(--red)',
                }}
              >
                Delete
              </button>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

function suggestedFormKey(region: string, name: string): string {
  if (!/[a-z0-9]/i.test(name.normalize('NFKD'))) return '';
  return `${region}-${name}`.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
}
